import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Select, MenuItem, IconButton, useMediaQuery, Dialog, AppBar, Toolbar, Button, Badge } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ButtonGroup from '@mui/material/ButtonGroup';
import ExplorerReports from 'features/frontoffice/explorerReport/explorerReport';
import StandardReports from 'features/frontoffice/standardReport';
import ExclusiveReports from 'features/frontoffice/exclusiveReport';
import CustomReports from 'features/frontoffice/customReport/customReport';
import AdHocReports from 'features/frontoffice/adHocReport/adHocReport';
import FavoriteReports from 'features/frontoffice/favoriteReport/favoriteReport';
import SmartCards from 'features/frontoffice/smartCards';
import { IconLayoutGrid, IconList, IconFilter  } from '@tabler/icons-react';
import CloseIcon from '@mui/icons-material/Close';
import StandardReportFilters from 'features/frontoffice/component/reportFilters';
import CustomReportFilters from 'features/frontoffice/customReport/reportFilters';
import AdHocFilters from 'features/frontoffice/adHocReport/reportFilters';
import SavedReportFilters from 'features/frontoffice/saved/reportFilters';
import Inspection from '../component/inspection';
import PillSelect from 'common/PillSelect';
import SearchField from 'common/SearchField';
import { hasSmartCardViewPermission, hasPermission } from 'utils/common';
import { AppConfig } from 'AppConfig';
import ReportSummary from 'features/landing/newHome/ReportSummary';
import { STANDARD_REPORT_TYPE, ALL_REPORT_TYPE, REPORT_TYPE_OPTIONS, DEFAULT_SORT_OPTIONS, REPORT_TYPE_ALIASES, REPORT_SMART_CARD_SORT_OPTIONS } from 'features/landing/newHome/NewHomeConfig';
import { useLocation } from 'react-router-dom';
import { showRestoreToast } from 'utils/showRestoreToast';
import { useSmartCardVdsTrigger } from 'features/frontoffice/useSmartCardVdsTrigger';

const COMPONENT_MAP = {
  standard: StandardReports,
  explorer: ExplorerReports,
  saved: FavoriteReports,
  exclusive: ExclusiveReports,
  custom: CustomReports,
  adhoc: AdHocReports,
  favorite: FavoriteReports,
  smart: SmartCards
};

const DEFAULT_REPORT_TYPE = STANDARD_REPORT_TYPE;
const REPORT_VIEW_MODE_STORAGE_KEY = 'reportsViewMode';
const DEFAULT_VIEW_MODE = 'grid';
const VIEW_MODES = new Set(['grid', 'list']);
const FILTERABLE_REPORT_TYPES = new Set(['standard', 'custom', 'adhoc', 'saved']);

const CONTENT_INSET_SX = { px: {xs:'16px', sm:'24px', md:'32px', lg: '40px', xl:'48px'}, width: '100%', mx: 'auto', maxWidth: '100%', boxSizing: 'border-box' };
const normalizeReportType = (value) => {
  if (!value) return DEFAULT_REPORT_TYPE;
  return REPORT_TYPE_ALIASES[value] || REPORT_TYPE_ALIASES[String(value).toLowerCase()] || DEFAULT_REPORT_TYPE;
};

const getStoredViewMode = () => {
  const storedViewMode = sessionStorage.getItem(REPORT_VIEW_MODE_STORAGE_KEY);
  return VIEW_MODES.has(storedViewMode) ? storedViewMode : DEFAULT_VIEW_MODE;
};

const getToggleButtonStyles = ({ isActive, isLeading }) => ({
  borderRadius: isLeading ? '16px 0 0 16px' : '0 16px 16px 0',
  borderTop: `1px solid ${isActive ? '#001A47' : '#71767A'}`,
  borderRight: `1px solid ${isActive ? '#001A47' : '#71767A'}`,
  borderBottom: `1px solid ${isActive ? '#001A47' : '#71767A'}`,
  borderLeft: isLeading ? `1px solid ${isActive ? '#001A47' : '#71767A'}` : 'none',
  backgroundColor: isActive ? '#001A47' : '#FFF',
  color: isActive ? '#FFF' : '#111111',
  '&:focus': {
    backgroundColor: isActive ? '#001A47' : '#FFF',
    boxShadow: 'none'
  },
  '&:hover': {
    backgroundColor: isActive ? '#001A47' : '#D8E6FF',
    borderColor: isActive ? '#001A47' : '#D8E6FF',
    color: isActive ? '#FFF' : '#001A47',
    boxShadow: 'none'
  },
  '&:active': {
    backgroundColor: isActive ? '#001A47' : '#FFF',
    boxShadow: 'none'
  },
  '&.Mui-focusVisible': {
    backgroundColor: isActive ? '#001A47' : '#FFF',
    boxShadow: 'none'
  }
});

const ConsolidatedReports = () => {
  const permissions = useSelector((state) => state.user.permissions);
  const user = useSelector((state) => state.user);
  const openReportViewDialog = useSelector((state) => state.fo.openReportViewDialog);
  const reportOriginComponent = useSelector((state) => state.fo.reportOriginComponent);
  const canViewSmartCards = hasSmartCardViewPermission(user);
  const canViewAdHoc = hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions);
  const isUserHydrated = !user.defaultUserState;
  const dispatch = useDispatch();

  // Refresh Smart Card values from /tbl/sc-vds when global filters change.
  useSmartCardVdsTrigger();

  const [reportsType, setReportsType] = useState(
    () => sessionStorage.getItem('reportsType') || DEFAULT_REPORT_TYPE
  );
  const location = useLocation();

  useEffect(() => {
    if (!isUserHydrated) return;
    const normalized = normalizeReportType(reportsType);
    const smartBlocked = !canViewSmartCards && normalized === 'smart';
    const adHocBlocked = !canViewAdHoc && normalized === 'adhoc';
    if (smartBlocked || adHocBlocked) {
      setReportsType(DEFAULT_REPORT_TYPE);
      sessionStorage.setItem('reportsType', DEFAULT_REPORT_TYPE);
    }
  }, [isUserHydrated, canViewSmartCards, canViewAdHoc, reportsType]);

  // Filters modal state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeBtnRef = useRef(null);
  const reportGridRef = useRef(null);

  // Selected filters (committed) and in-progress selection in the modal
  const [pendingSelectedFilterTags, setPendingSelectedFilterTags] = useState([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);

  const getTypeDefaultSortBy = useCallback((type) => {
    return normalizeReportType(type) === 'smart' ? 'needs_attention' : 'alphabetical_asc';
  }, []);

  const getDefaultSortBy = useCallback(() => {
    const fromSession = sessionStorage.getItem('sortBy');
    if (fromSession) {
      return fromSession;
    }
    const computed = getTypeDefaultSortBy(reportsType);
    return computed;
  }, [reportsType, getTypeDefaultSortBy]);

  useEffect(()=>{
   if(location.state?.restored){
     showRestoreToast("All Hidden Smart Cards Restored" , "331px" , "233px" , "#005C2C" , false);
     window.history.replaceState({}, '');
   }
  },[location.state]);

  const [selectedReportCategory, setSelectedReportCategory] = useState(getDefaultSortBy);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(getStoredViewMode);

  useEffect(() => {
    const storedReportType = sessionStorage.getItem('reportsType');
    if (!storedReportType) {
      sessionStorage.setItem('reportsType', DEFAULT_REPORT_TYPE);
    }
    setSelectedFilterTags([]);
    setPendingSelectedFilterTags([]);
  }, []);


  const hasHandledInitialScrollRef = useRef(false);
  // Scroll to report grid if navigated from "See All Smart Cards", otherwise scroll to top
  useEffect(()=> {
    if(openReportViewDialog) return;

    const shouldSScroll = sessionStorage.getItem('scrollToReports') === 'true' ||
      sessionStorage.getItem('cameFromHiddenSmartCards') === 'true';

      if(!shouldSScroll) {
        if(!hasHandledInitialScrollRef.current) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth',  });
          hasHandledInitialScrollRef.current = true;
        }
        return;
      }

      hasHandledInitialScrollRef.current = true;

      sessionStorage.removeItem('scrollToReports');
      sessionStorage.removeItem('cameFromHiddenSmartCards');
      sessionStorage.removeItem('reportOrigin');

      const el = reportGridRef?.current || document.getElementById('reports-section');
      if(!el){
        return;
      }

      let resizeTimer;
      const observer = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          observer.disconnect();
        }, 400);
      });
      observer.observe(document.body);

      const forceTimer = setTimeout(()=>{
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        observer.disconnect();
      }, 3000);

      return ()=>{
        observer.disconnect();
        clearTimeout(resizeTimer);
        clearTimeout(forceTimer);
      }

  }, [openReportViewDialog]);

  // 320-767px: mobile, 768-1279px: tablet, 1280px+: desktop
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1279px)');
  // <400px = Figma single-card mobile layout (16px side padding).
  // ≥400px = standard 24px side padding.
  const isAtLeast400 = useMediaQuery('(min-width:400px)');
  const normalizedReportsType = normalizeReportType(reportsType);
  const shouldShowMobileFilters = FILTERABLE_REPORT_TYPES.has(normalizedReportsType);
  const searchPlaceholder = useMemo(() => {
    switch (normalizedReportsType) {
      case 'custom':
        return 'Search Custom Reports';
      case 'saved':
        return 'Search Saved Reports';
      case 'standard':
        return 'Search Standard Reports';
      case 'explorer':
        return 'Search Explorer Reports';
      case 'smart':
        return 'Search Smart Cards';
      default:
        return 'Search Reports';
    }
  }, [normalizedReportsType]);
  const sortOptions = useMemo(() => (normalizedReportsType === 'smart' ? REPORT_SMART_CARD_SORT_OPTIONS : DEFAULT_SORT_OPTIONS), [normalizedReportsType]);

  const reportTypeMenuOptions = useMemo(() => REPORT_TYPE_OPTIONS.filter((option) => {
    if (!option?.isDivider && option?.value === ALL_REPORT_TYPE) return false;
    if (!canViewSmartCards && option?.value === AppConfig.REPORT_CATEGORY.SMART.code) return false;
    if (!canViewAdHoc && option?.value === AppConfig.REPORT_CATEGORY.AD_HOC.code) return false;
    return true;
  }), [canViewSmartCards, canViewAdHoc]);

  const reportTypeMenuItems = useMemo(() => reportTypeMenuOptions.map((option) => (
    option.isDivider ? (
      <Box
        key="my-reports-heading"
        sx={{
          p: '24px',
          fontFamily: 'Noto Sans',
          fontSize: '16px',
          fontWeight: 600,
          fontStyle: 'normal',
          color: '#121A22',
          lineHeight: '150%',
          borderTop: '1px solid var(--ctev-gray, #D5D5D5)'
        }}>
        {option.label}
      </Box>
    ) : (
      <MenuItem
        key={option.value}
        value={option.value}
        sx={{
          display: 'flex',
          padding: '16px 24px',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px',
          fontWeight: 400,
          alignSelf: 'stretch',
          '&.MuiButtonBase-root:hover': {
            backgroundColor: '#F2F2F2 !important'
          },
          '&.MuiButtonBase-root:focus': {
            backgroundColor: '#F2F2F2 !important'
          },
          '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
            bgcolor: 'rgb(213, 221, 237) !important',
            color: '#2F54A5 !important',
            fontWeight: 700
          }
        }}>
        {option.label}
      </MenuItem>
    )
  )), [reportTypeMenuOptions]);

  const prevReportsTypeRef = useRef(normalizedReportsType);
  useEffect(() => {
    const isTypeChange = prevReportsTypeRef.current !== normalizedReportsType;
    prevReportsTypeRef.current = normalizedReportsType;

    let desiredDefault;

    // When switching INTO Smart Cards, always use needs_attention regardless of session sort
    if (isTypeChange && normalizedReportsType === 'smart') {
      desiredDefault = 'needs_attention';
    } else if (isTypeChange) {
      // When switching to other types, use their type default
      desiredDefault = getTypeDefaultSortBy(normalizedReportsType);
    } else {
      // Not a type change - reuse session sort if valid for this type, else use type default
      const fromSession = sessionStorage.getItem('sortBy');
      const typeDefault = getTypeDefaultSortBy(normalizedReportsType);
      desiredDefault = sortOptions?.some((opt) => opt.value === fromSession) ? fromSession : typeDefault;
    }

    const nextValue = sortOptions?.some((opt) => opt.value === desiredDefault) ? desiredDefault : sortOptions?.[0]?.value;
    if (nextValue) handleSortByChange(nextValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedReportsType, sortOptions, getTypeDefaultSortBy]);

  useEffect(() => {
    if (!shouldShowMobileFilters) {
      setSelectedFilterTags([]);
      setPendingSelectedFilterTags([]);
      setFiltersOpen(false);
    }
  }, [shouldShowMobileFilters]);

  const Reports = COMPONENT_MAP[normalizedReportsType] || StandardReports;

  // Stable no-op filter for ReportFilters (keeps its API satisfied)
  const filter = useCallback(() => {}, []);

  // Handlers
  const handleReportsTypeChange = (event) => {
    const val = event.target.value;
    setReportsType(val);
    setSearchTerm('');
    sessionStorage.setItem('reportsType', val);
  };

  const handleSortByChange = (val) => {
    setSelectedReportCategory(val);
    sessionStorage.setItem('sortBy', val);
  };

  const handleViewModeChange = (mode, event) => {
    if (!VIEW_MODES.has(mode)) return;
    setViewMode(mode);
    sessionStorage.setItem(REPORT_VIEW_MODE_STORAGE_KEY, mode);
    event?.currentTarget?.blur?.();
  };

  const handleOpenFilters = () => {
    // Blur the trigger to avoid aria-hidden focus warning
    triggerRef.current?.blur();
    setFiltersOpen(true);
  };

  const handleClearFilters = () => {
    setPendingSelectedFilterTags([]);
    setSelectedFilterTags([]);
    setFilterResetTrigger((prev) => prev + 1);
  };

  const handleCloseFilters = () => setFiltersOpen(false);

  const handleApply = () => {
    setSelectedFilterTags(pendingSelectedFilterTags);
    setFiltersOpen(false);
  };

  // Move focus into dialog and optionally make background inert to avoid focus issues
  useEffect(() => {
    const root = document.getElementById('root');
    if (filtersOpen) {
      setTimeout(() => closeBtnRef.current?.focus(), 0);
      root?.setAttribute?.('inert', '');
    } else {
      root?.removeAttribute?.('inert');
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, [filtersOpen]);

  return isMobile ? (
    <Box
      sx={{ mx: '-60px', px: isAtLeast400 ? '24px' : '16px', mt: 2, pb: 6 }}
      ref={reportGridRef}
    >
      <Grid container justifyContent="space-between">
        <Grid item size={{ xs: 12 }}>
          <Inspection isReports showRefreshStatus />
          <ReportSummary isReports />
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                color: 'var(--black-heading-text-icons, #000)',
                fontFamily: 'Noto Sans',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                textEdge: 'cap',
                leadingTrim: 'both'
              }}
               >
              Report Type
            </Typography>
            <PillSelect
              value={reportsType}
              onChange={(e) => handleReportsTypeChange({ target: { value: e.target.value } })}
              options={reportTypeMenuOptions}
              sx={{ height: '48px' }}
            />
            <Typography
              sx={{
                color: 'var(--black-heading-text-icons, #000)',
                fontFamily: 'Noto Sans',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                textEdge: 'cap',
                leadingTrim: 'both'
              }}
              >
              Sort by
            </Typography>
            <PillSelect
              value={selectedReportCategory}
              onChange={(e) => handleSortByChange(e.target.value)}
              options={sortOptions}
              sx={{ height: '48px' }}
            />
            <Typography sx={{ fontFamily: 'Noto Sans', fontWeight: 600, fontSize: 14 }}></Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <SearchField
                placeholder={searchPlaceholder}
                width="100%"
                height="48px"
                mb={0}
                searchTerm={searchTerm}
                searchReports={{ setSearchTerm }}
              />

              {shouldShowMobileFilters && (
                <Button
                  variant="outlined"
                  onClick={handleOpenFilters}
                  sx={{ borderRadius: '24px', textTransform: 'none', height: 44, background: 'var(--white-bi, #FFF)', border: '1px solid var(--Blue-600---Primary-CTA, #2F54A5)' }}
                >
                  <IconFilter size={18} style={{ marginRight: 8 }} />
                  Filter
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ mt: 3, mb: 3 }}>
            <Reports selectedFilterTags={selectedFilterTags} isMobile={isMobile} searchTerm={searchTerm} 
            sortBy={selectedReportCategory} />
        </Box>
      </Box>

      <Dialog fullScreen open={filtersOpen} onClose={handleCloseFilters} PaperProps={{ sx: { height: '100%' } }}>
        <AppBar sx={{ position: 'relative', boxShadow: 'none', bgcolor: '#fff', color: '#121A22',
            borderBottom: '1px solid var(--Gray---Line-Stroke, #71767A)', width: '100%'
         }}>
          <Toolbar
            disableGutters
            sx={{
              width: '100%',
              minHeight: '73px',
              height:'73px',
              px: '16px',
              pt: '24px',
              pb: '24px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
            }}>
            <IconButton
              ref={closeBtnRef}
              edge="end"
              color="inherit"
              onClick={handleCloseFilters}
              aria-label="close"
              sx={{
                p: 0,
                  width: '24px',
                  height: '24px'
              }}
            >
              <CloseIcon sx={{fontSize:'20px'}}/>
            </IconButton>
          </Toolbar>
        </AppBar>

         <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 73px)', overflow: 'hidden' }}>
          <Box sx={{ px: '16px', pt: '24px', pb: '16px', flexShrink: 0 }}>
            <Typography
              sx={{
                color: '#000',
                fontFamily: 'Rubik',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                letterSpacing: '0.16px',
              }}
              variant="h6"
              component="div">
              Filter
            </Typography>
          </Box>

          <Box
            sx={{
              px: '16px',
              pt: '0',
              flex: 1,
              overflowY: 'auto',
            }}
            >
            {normalizedReportsType === 'standard' ? (
              <StandardReportFilters setData={() => {}} filter={filter} onSelectionChange={setPendingSelectedFilterTags} 
              isMobile={isMobile} resetSelectionTrigger={filterResetTrigger}/>
            ) : normalizedReportsType === 'custom' ? (
              <CustomReportFilters searchKey="" data={[]} isMobile={isMobile} onSelectionChange={setPendingSelectedFilterTags} 
              resetSelectionTrigger={filterResetTrigger}/>
            ) : normalizedReportsType === 'adhoc' ? (
              <AdHocFilters setData={() => {}} filter={filter} onSelectionChange={setPendingSelectedFilterTags} 
              isMobile={isMobile} resetSelectionTrigger={filterResetTrigger} />
            ) : normalizedReportsType === 'saved' ? (
              <SavedReportFilters setData={() => {}} onSelectionChange={setPendingSelectedFilterTags} 
              isMobile={isMobile} resetSelectionTrigger={filterResetTrigger}/>
            ) : null}
          </Box>

          <Box
            sx={{
              borderTop: '1px solid var(--Gray---Line-Stroke, #71767A)',
              px: '16px',
              py: '24px',
              display: 'flex',
               flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              bgcolor: '#FFFFFF',
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: '24px',
                flex: 1,
                minWidth: 0,
                height: '44px',
                textTransform: 'none',
              }}
              onClick={handleClearFilters}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  lineHeight: '16px',
                  textAlign: 'center',
                }}
              >
                Clear All
              </Typography>
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: '24px',
                flex: 1,
                minWidth: 0,
                height: '44px',
                textTransform: 'none',
              }}
              onClick={handleApply}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  lineHeight: '16px',
                  textAlign: 'center',
                  color: '#FFFFFF',
                }}
              >
                Apply
              </Typography>
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  ) : isTablet ? (
    <Box
      sx={{ bgcolor: '#FFFFFF', px: '24px', mx: '-60px', minHeight: 'calc(100vh - 70px)', pb: 60, overflow: 'visible' }}
      ref={reportGridRef}
    >
      <Box sx={{ ...CONTENT_INSET_SX, pt: 2 }}>
        <Inspection isReports showRefreshStatus />
        <ReportSummary isReports />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: '1 1 320px', minWidth: '320px' }}>
            <Box sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Noto Sans', color: '#000', mb: '8px' }}>
              Report Type
            </Box>
            <Select
              disableUnderline
              id="reports-type"
              value={reportsType}
              onChange={handleReportsTypeChange}
              sx={{
                width: '100%',
                borderRadius: '24px',
                border: '1px solid var(--ctev-blue, #2F54A5)',
                background: '#FFF',
                display: 'flex',
                padding: '6px 16px',
                flexDirection: 'column',
                gap: '8px',
                alignSelf: 'stretch',
                '& .MuiSelect-select': {
                  color: 'var(--ctev-black, #000)',
                  fontFamily: 'Noto Sans',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  paddingLeft: '24px'
                },
                '& .MuiSvgIcon-root': {
                  marginRight: '14px',
                  fontSize: '24px',
                  color: '#1C1B1F'
                }
              }}
            MenuProps={{
              sx: {
                '.MuiMenu-list': {
                  paddingTop: '0px',
                  paddingBottom: '0px'
                },
                '.MuiMenu-paper li.MuiMenuItem-root.Mui-selected:hover':{
                    color: '#2F54A5 !important',
                  }
              },
              PaperProps: {
                sx: {
                  borderRadius: '24px',
                  gap: '24px',
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid var(--ctev-blue, #2F54A5)',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.1)'
                }
              }
            }}>
            {reportTypeMenuOptions.map((opt) => {
              if (opt.isDivider) {
                return (
                  <Box
                    key={`divider-${opt.label}`}
                    sx={{
                      p: '16px 16px 8px 16px;',
                      fontFamily: 'Noto Sans',
                      fontSize: '16px',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      color: '#121A22',
                      lineHeight: '150%',
                      borderTop: '1px solid var(--ctev-gray, #D5D5D5)'
                    }}
                  >
                    {opt.label}
                  </Box>
                );
              }
              return (
                <MenuItem
                  value={opt.value}
                  key={opt.value}
                  sx={{
                    display: 'flex',
                    padding: '16px 24px',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: 400,
                    alignSelf: 'stretch',
                    '&.MuiButtonBase-root:hover': {
                      backgroundColor: '#F2F2F2 !important'
                      },
                    '&.MuiButtonBase-root:focus': {
                      backgroundColor: '#F2F2F2 !important'
                    }, '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                      bgcolor: 'rgb(213, 221, 237) !important',
                      color: '#2F54A5 !important',
                      fontWeight: 700
                    }
                  }}
                >
                  {opt.label}
                </MenuItem>
              );
            })}
            </Select>
        </Box>
          <Box sx={{ flex: '1 1 320px', minWidth: '320px' }}>
            <Box sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Noto Sans', color: '#000', mb: '8px' }}>
              Sort by
            </Box>
            <PillSelect
              value={selectedReportCategory}
              onChange={(e) => handleSortByChange(e.target.value)}
              options={sortOptions}
              width="100%"
            />
          </Box>
        </Box>
        <Box sx={{ mb: 3, width: '100%' }}>
          <SearchField
            key={reportsType}
            width="100%"
            height={48}
            placeholder={searchPlaceholder}
            mb={0}
            searchTerm={searchTerm}
            reportType={reportsType}
            labelOptions={REPORT_TYPE_OPTIONS}
            allbackOptions={reportTypeMenuOptions
                .filter((option) => !option.isDivider)
                .map((option) => ({ val: normalizeReportType(option.value), displayText: option.label }))}
            normalizeReportType={normalizeReportType}
            searchReports={{ setSearchTerm }}
          />
        </Box>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Reports isTablet={isTablet} embedded={normalizedReportsType === 'smart'} searchTerm={searchTerm} sortBy={selectedReportCategory} />
          </Box>
        </Box>
      </Box>
      
  ) : (
    <Box sx={{ bgcolor: '#FFFFFF', mx: '-60px', minHeight: 'calc(100vh - 70px)', pb: 20, overflow: 'visible' }}>
      <Box sx={{ ...CONTENT_INSET_SX, pt: 2 }}>
        <Inspection isReports showRefreshStatus />
      </Box>
      <ReportSummary isReports />
      <Box sx={CONTENT_INSET_SX}>
        <Box
          id='reports-section'
          ref={reportGridRef}
          display="flex"
          flexDirection="row"
          flexWrap="nowrap"
          justifyContent="flex-start"
          alignItems="flex-end"
          sx={{
            gap: '24px',
            width: '100%'
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '258px', flexShrink: 0 }}>
            <Box sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Noto Sans', color: '#000' }}>Report Type</Box>
            <PillSelect
              value={reportsType}
              onChange={handleReportsTypeChange}
              options={reportTypeMenuOptions}
              width={258}
              menuMaxHeight={320}
            />
          </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '258px', flexShrink: 0 }}>
         <Box sx={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Noto Sans', color: '#000' }}>Sort by</Box>
            <PillSelect
              value={selectedReportCategory}
              onChange={(e) => handleSortByChange(e.target.value)}
              options={sortOptions}
              width={258}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: '1 1 0', minWidth: 0, flexDirection: 'column', gap: '4px' }}>
            <SearchField
              key = {reportsType}
              width="100%"
              height={48}
              mb={0}
              searchTerm={searchTerm}
              reportType={reportsType}
              labelOptions={REPORT_TYPE_OPTIONS}
              fallbackOptions={reportTypeMenuOptions
                .filter((option) => !option.isDivider)
                .map((option) => ({ val: normalizeReportType(option.value), displayText: option.label }))}
              normalizeReportType={normalizeReportType}
              searchReports={{
                setSearchTerm
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <ButtonGroup
              variant="contained"
              aria-label="Grid and List"
              sx={{
                borderRadius: '16px',
                height: '48px',
                width: '112px',
                overflow: 'hidden',
                boxShadow: 'none',
                '& .MuiButton-root': {
                  minWidth: '56px',
                  padding: '0 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  alignSelf: 'stretch',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontFamily: 'Noto Sans',
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: '16.8px'
                }
              }}>
              <Button
                aria-pressed={viewMode === 'grid'}
                onClick={(event) => handleViewModeChange('grid', event)}
                disableRipple
                disableFocusRipple
                sx={getToggleButtonStyles({ isActive: viewMode === 'grid', isLeading: true })}>
                <IconLayoutGrid size={24} stroke={1.75} />
              </Button>
              <Button
                aria-pressed={viewMode === 'list'}
                onClick={(event) => handleViewModeChange('list', event)}
                disableRipple
                disableFocusRipple
                sx={getToggleButtonStyles({ isActive: viewMode === 'list', isLeading: false })}>
                <IconList size={24} stroke={1.75} />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        <Box sx={{ mt: '24px' }}>
          <Box sx={{ mb: 3 }}>
            <Reports 
              searchTerm={searchTerm} 
              sortBy={selectedReportCategory} 
              reportType={reportsType} 
              embedded
              viewMode={viewMode}
             />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ConsolidatedReports;